import { useEffect, useState, useCallback } from "react";
import api from "../../lib/api";
import { PageHeader, Card, Loading, inputCls, labelCls, btnPrimary, btnSecondary } from "../../components/admin/common";
import { FileSpreadsheet, FileDown, Sheet, Search, ChevronUp, ChevronDown, X, Filter } from "lucide-react";
import { toast } from "sonner";

const STANDARDS = Array.from({ length: 12 }, (_, i) => `Standard ${i + 1}`);
const STATUSES = ["pending", "completed", "rejected", "duplicate"];
const EMPTY_FILTERS = { school_id: [], program_id: [], level_id: [], batch_id: [], standard: [], payment_status: [], search: "", date_from: "", date_to: "" };

function MultiCheck({ title, options, selected, onToggle, onAll, onClear, testid }) {
  return (
    <div>
      <div className="flex items-center justify-between mb-1"><span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">{title}</span>
        <span className="flex gap-2 text-[11px]"><button onClick={onAll} className="text-blue-600 hover:underline">All</button><button onClick={onClear} className="text-slate-400 hover:underline">Clear</button></span></div>
      <div className="max-h-28 overflow-y-auto border border-slate-200 rounded-lg p-2 bg-slate-50 space-y-1">
        {options.length === 0 && <div className="text-xs text-slate-400">None</div>}
        {options.map((o) => (
          <label key={o.value} className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
            <input type="checkbox" checked={selected.includes(o.value)} onChange={() => onToggle(o.value)} data-testid={`${testid}-opt`} className="rounded border-slate-300 text-blue-600" />
            <span className="truncate">{o.label}</span>
          </label>
        ))}
      </div>
    </div>
  );
}

export default function Reports() {
  const [groups, setGroups] = useState([]);
  const [selected, setSelected] = useState([]); // ordered field keys
  const [labelMap, setLabelMap] = useState({});
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [masters, setMasters] = useState({ schools: [], programs: [], levels: [], batches: [] });
  const [preview, setPreview] = useState(null); // {count, columns, rows}
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get("/export/data/fields").then(({ data }) => {
      setGroups(data.groups);
      const m = {}; data.groups.forEach((g) => g.fields.forEach((f) => (m[f.key] = f.label)));
      setLabelMap(m);
      setSelected(["student_id", "student_name", "parent_name", "mobile", "email", "standard", "payment_status"].filter((k) => m[k]));
    });
    Promise.all([api.get("/schools"), api.get("/programs"), api.get("/levels"), api.get("/batches")])
      .then(([s, p, l, b]) => setMasters({ schools: s.data, programs: p.data, levels: l.data, batches: b.data })).catch(() => {});
  }, []);

  const toggleField = (k) => setSelected((s) => (s.includes(k) ? s.filter((x) => x !== k) : [...s, k]));
  const selectAllFields = () => setSelected(groups.flatMap((g) => g.fields.map((f) => f.key)));
  const clearAllFields = () => setSelected([]);
  const toggleGroup = (g) => {
    const keys = g.fields.map((f) => f.key);
    const allOn = keys.every((k) => selected.includes(k));
    setSelected((s) => (allOn ? s.filter((k) => !keys.includes(k)) : [...s, ...keys.filter((k) => !s.includes(k))]));
  };
  const moveCol = (i, dir) => { const j = i + dir; if (j < 0 || j >= selected.length) return; const c = [...selected]; [c[i], c[j]] = [c[j], c[i]]; setSelected(c); };

  const setF = (key, vals) => setFilters((f) => ({ ...f, [key]: vals }));
  const toggleFilter = (key, v) => setFilters((f) => ({ ...f, [key]: f[key].includes(v) ? f[key].filter((x) => x !== v) : [...f[key], v] }));
  const reset = () => { setFilters(EMPTY_FILTERS); setPreview(null); };

  const body = useCallback(() => ({ fields: selected, order: selected, filters }), [selected, filters]);

  const apply = async () => {
    if (selected.length === 0) { toast.error("Select at least one field"); return; }
    setLoading(true);
    try { const { data } = await api.post("/export/data/preview", body()); setPreview(data); }
    catch (e) { toast.error("Preview failed"); } finally { setLoading(false); }
  };

  const download = async (fmt) => {
    if (selected.length === 0) { toast.error("Select at least one field"); return; }
    try {
      const res = await api.post("/export/data/download", { ...body(), fmt }, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = `gdiot_pay_export.${fmt === "xlsx" ? "xlsx" : "csv"}`; a.click();
      URL.revokeObjectURL(url); toast.success("Export downloaded");
    } catch (e) { toast.error("Export failed"); }
  };

  const chips = [
    ...filters.school_id.map((v) => ({ k: "school_id", v, l: `School: ${masters.schools.find((x) => x.id === v)?.name || v}` })),
    ...filters.program_id.map((v) => ({ k: "program_id", v, l: `Program: ${masters.programs.find((x) => x.id === v)?.name || v}` })),
    ...filters.level_id.map((v) => ({ k: "level_id", v, l: `Level: ${masters.levels.find((x) => x.id === v)?.name || v}` })),
    ...filters.batch_id.map((v) => ({ k: "batch_id", v, l: `Batch: ${masters.batches.find((x) => x.id === v)?.name || v}` })),
    ...filters.standard.map((v) => ({ k: "standard", v, l: `Std: ${v}` })),
    ...filters.payment_status.map((v) => ({ k: "payment_status", v, l: `Status: ${v}` })),
  ];

  const opt = (arr, lk = "name") => arr.map((x) => ({ value: x.id, label: x[lk] }));

  return (
    <div>
      <PageHeader title="Export Data" subtitle="Choose the fields you need, filter the students you want, and download exactly that data." />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Field selection */}
        <Card className="p-5">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-head font-semibold text-slate-900">Select Data to Export</h3>
            <div className="flex gap-2 text-xs"><button onClick={selectAllFields} className="text-blue-600 font-semibold hover:underline" data-testid="select-all-fields">Select All</button><button onClick={clearAllFields} className="text-slate-400 hover:underline" data-testid="clear-all-fields">Clear All</button></div>
          </div>
          <div className="space-y-4 max-h-[420px] overflow-y-auto pr-1">
            {groups.map((g) => {
              const keys = g.fields.map((f) => f.key); const allOn = keys.every((k) => selected.includes(k));
              return (
                <div key={g.group}>
                  <label className="flex items-center gap-2 text-xs font-bold text-slate-600 uppercase tracking-wider mb-1.5 cursor-pointer">
                    <input type="checkbox" checked={allOn} onChange={() => toggleGroup(g)} className="rounded border-slate-300 text-blue-600" /> {g.group}
                  </label>
                  <div className="grid grid-cols-2 gap-1.5 pl-1">
                    {g.fields.map((f) => (
                      <label key={f.key} className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
                        <input type="checkbox" checked={selected.includes(f.key)} onChange={() => toggleField(f.key)} data-testid={`field-${f.key}`} className="rounded border-slate-300 text-blue-600" />
                        <span className="truncate">{f.label}</span>
                      </label>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
          {selected.length > 0 && (
            <div className="mt-4 pt-4 border-t border-slate-100">
              <div className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">Column Order ({selected.length})</div>
              <div className="space-y-1 max-h-40 overflow-y-auto">
                {selected.map((k, i) => (
                  <div key={k} className="flex items-center gap-2 bg-slate-50 border border-slate-200 rounded-lg px-2.5 py-1.5 text-sm">
                    <span className="flex-1 truncate">{labelMap[k] || k}</span>
                    <button onClick={() => moveCol(i, -1)} className="text-slate-400 hover:text-slate-700"><ChevronUp className="w-3.5 h-3.5" /></button>
                    <button onClick={() => moveCol(i, 1)} className="text-slate-400 hover:text-slate-700"><ChevronDown className="w-3.5 h-3.5" /></button>
                    <button onClick={() => toggleField(k)} className="text-slate-400 hover:text-rose-600"><X className="w-3.5 h-3.5" /></button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </Card>

        {/* Filters */}
        <Card className="p-5">
          <h3 className="font-head font-semibold text-slate-900 mb-3 flex items-center gap-2"><Filter className="w-4 h-4 text-blue-600" /> Filter Students</h3>
          <div className="grid grid-cols-2 gap-3">
            <MultiCheck title="School" testid="ex-school" options={opt(masters.schools)} selected={filters.school_id} onToggle={(v) => toggleFilter("school_id", v)} onAll={() => setF("school_id", masters.schools.map((x) => x.id))} onClear={() => setF("school_id", [])} />
            <MultiCheck title="Program" testid="ex-program" options={opt(masters.programs)} selected={filters.program_id} onToggle={(v) => toggleFilter("program_id", v)} onAll={() => setF("program_id", masters.programs.map((x) => x.id))} onClear={() => setF("program_id", [])} />
            <MultiCheck title="Level" testid="ex-level" options={opt(masters.levels)} selected={filters.level_id} onToggle={(v) => toggleFilter("level_id", v)} onAll={() => setF("level_id", masters.levels.map((x) => x.id))} onClear={() => setF("level_id", [])} />
            <MultiCheck title="Batch" testid="ex-batch" options={opt(masters.batches)} selected={filters.batch_id} onToggle={(v) => toggleFilter("batch_id", v)} onAll={() => setF("batch_id", masters.batches.map((x) => x.id))} onClear={() => setF("batch_id", [])} />
            <MultiCheck title="Standard / Class" testid="ex-standard" options={STANDARDS.map((s) => ({ value: s, label: s }))} selected={filters.standard} onToggle={(v) => toggleFilter("standard", v)} onAll={() => setF("standard", STANDARDS)} onClear={() => setF("standard", [])} />
            <MultiCheck title="Payment Status" testid="ex-status" options={STATUSES.map((s) => ({ value: s, label: s }))} selected={filters.payment_status} onToggle={(v) => toggleFilter("payment_status", v)} onAll={() => setF("payment_status", STATUSES)} onClear={() => setF("payment_status", [])} />
          </div>
          <div className="grid grid-cols-2 gap-3 mt-3">
            <div><label className={labelCls}>Registration From</label><input type="date" className={inputCls} value={filters.date_from} onChange={(e) => setFilters({ ...filters, date_from: e.target.value })} /></div>
            <div><label className={labelCls}>Registration To</label><input type="date" className={inputCls} value={filters.date_to} onChange={(e) => setFilters({ ...filters, date_to: e.target.value })} /></div>
          </div>
          <div className="relative mt-3">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input className={`${inputCls} pl-10`} placeholder="Search name, Student ID, Registration ID, mobile, email..." value={filters.search} onChange={(e) => setFilters({ ...filters, search: e.target.value })} data-testid="ex-search" />
          </div>
          {chips.length > 0 && (
            <div className="flex flex-wrap gap-2 mt-3">
              {chips.map((c, i) => <span key={i} className="inline-flex items-center gap-1.5 bg-blue-50 text-blue-700 border border-blue-100 rounded-full px-3 py-1 text-xs font-medium">{c.l}<button onClick={() => toggleFilter(c.k, c.v)}><X className="w-3 h-3" /></button></span>)}
            </div>
          )}
          <div className="flex gap-2 mt-4">
            <button className={btnPrimary} onClick={apply} data-testid="apply-filter-btn"><Filter className="w-4 h-4" /> Apply & Preview</button>
            <button className={btnSecondary} onClick={reset} data-testid="reset-filter-btn"><X className="w-4 h-4" /> Reset</button>
          </div>
        </Card>
      </div>

      {/* Preview & export */}
      <Card className="mt-6 p-5">
        <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
          <div className="text-sm text-slate-600">Matching Students <span className="font-head font-bold text-slate-900 text-lg ml-1" data-testid="matching-count">{preview ? preview.count : "—"}</span></div>
          <div className="flex gap-2">
            <button className={btnSecondary} onClick={() => download("xlsx")} disabled={!preview || preview.count === 0} data-testid="download-excel"><FileSpreadsheet className="w-4 h-4" /> Excel</button>
            <button className={btnSecondary} onClick={() => download("csv")} disabled={!preview || preview.count === 0} data-testid="download-csv"><FileDown className="w-4 h-4" /> CSV</button>
            <button className={btnSecondary} onClick={() => download("csv")} disabled={!preview || preview.count === 0} data-testid="download-gsheets"><Sheet className="w-4 h-4" /> Google Sheets</button>
          </div>
        </div>
        {loading ? <Loading /> : !preview ? (
          <p className="text-sm text-slate-400 text-center py-6">Select fields and filters, then click Apply & Preview.</p>
        ) : preview.count === 0 ? (
          <div className="text-center py-8"><p className="font-semibold text-slate-700">No students found</p><p className="text-sm text-slate-400 mt-1">No records match the selected filters. Please change your filters and try again.</p></div>
        ) : (
          <div className="overflow-x-auto">
            <p className="text-xs text-slate-400 mb-2">Showing first {preview.rows.length} of {preview.count} matching students.</p>
            <table className="w-full text-sm">
              <thead><tr className="border-b border-slate-100 text-left text-xs uppercase tracking-wider text-slate-500">{preview.columns.map((c) => <th key={c.key} className="px-3 py-2 font-semibold whitespace-nowrap">{c.label}</th>)}</tr></thead>
              <tbody>
                {preview.rows.map((r, i) => (
                  <tr key={i} className="border-b border-slate-50" data-testid="preview-row">{preview.columns.map((c) => <td key={c.key} className="px-3 py-2 whitespace-nowrap text-slate-700">{String(r[c.key] ?? "")}</td>)}</tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}
